import React, { useState, useRef, useCallback } from 'react';

const DecimalRating = ({ value = 0, onChange, label = "Rating" }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [hoverValue, setHoverValue] = useState(null);
  const sliderRef = useRef(null);

  const displayValue = hoverValue !== null ? hoverValue : value;

  const getValueFromPosition = useCallback((clientX) => {
    if (!sliderRef.current) return 0;
    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    // 0.1 to 10.0 range (99 steps + 0.1 minimum)
    const rawValue = 0.1 + (percentage * 9.9);
    return Math.round(rawValue * 10) / 10; // Round to 1 decimal place
  }, []);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    const newValue = getValueFromPosition(e.clientX);
    onChange(newValue);
    setHoverValue(newValue);
  }, [getValueFromPosition, onChange]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging && !hoverValue) return;
    const newValue = getValueFromPosition(e.clientX);

    if (isDragging) {
      onChange(newValue);
    }
    setHoverValue(newValue);
  }, [isDragging, hoverValue, getValueFromPosition, onChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setHoverValue(null);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!isDragging) {
      setHoverValue(null);
    }
  }, [isDragging]);

  // Touch events for mobile
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    const touch = e.touches[0];
    const newValue = getValueFromPosition(touch.clientX);
    onChange(newValue);
    setHoverValue(newValue);
  }, [getValueFromPosition, onChange]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) return;
    const touch = e.touches[0];
    const newValue = getValueFromPosition(touch.clientX);
    onChange(newValue);
    setHoverValue(newValue);
  }, [isDragging, getValueFromPosition, onChange]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setHoverValue(null);
  }, []);

  // Attach global mouse events when dragging
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Calculate position percentage (0.1 = 0%, 10.0 = 100%)
  const percentage = Math.max(0, Math.min(100, ((displayValue - 0.1) / 9.9) * 100));

  const getLabel = (val) => {
    if (val >= 9.5) return "Masterpiece";
    if (val >= 8.5) return "Excellent";
    if (val >= 7.5) return "Very Good";
    if (val >= 6.5) return "Good";
    if (val >= 5.5) return "Decent";
    if (val >= 4.5) return "Average";
    if (val >= 3.5) return "Below Average";
    if (val >= 2.5) return "Poor";
    if (val >= 1.5) return "Very Poor";
    return "Awful";
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="block text-gray-700 font-semibold text-lg">{label}</label>

      {/* Value Display */}
      <div className="flex items-center gap-3">
        <span className="text-3xl font-bold text-cling-pink min-w-[80px]">
          {displayValue > 0 ? displayValue.toFixed(1) : '—'}
        </span>
        <span className="text-gray-500 text-lg">/10</span>
        <span className="text-sm text-gray-600 ml-2">
          {displayValue > 0 ? getLabel(displayValue) : ''}
        </span>
      </div>

      {/* Slider */}
      <div
        ref={sliderRef}
        className={`relative h-3 bg-gray-200 rounded-full cursor-pointer select-none transition-all duration-150 ${
          isDragging ? 'bg-gray-300' : ''
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
      >
        {/* Progress Track */}
        <div
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-indigo-400 to-cling-pink rounded-full transition-all duration-150"
          style={{ width: `${percentage}%` }}
        />

        {/* Slider Handle */}
        <div
          className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 bg-white border-3 border-gray-400 rounded-full shadow-lg transition-all duration-150 ${
            isDragging ? 'scale-110 border-indigo-600' : 'hover:scale-105'
          }`}
          style={{ left: `calc(${percentage}% - 12px)` }}
        />
      </div>

      {/* Scale Markers */}
      <div className="flex justify-between text-xs text-gray-400 px-1">
        <span>0.1</span>
        <span>2.5</span>
        <span>5.0</span>
        <span>7.5</span>
        <span>10.0</span>
      </div>
    </div>
  );
};

export default DecimalRating;